import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../../../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { ProjectsTable } from '../../tables';
import { useState, useMemo } from 'react';
import { filterProjectsForList, isProjectCompleted } from '../../../utils/projects';
const ProjectFilterToggle = ({ label, checked, onChange }) => {
    const toggle = () => onChange(!checked);

    return (
        <div className="archive-toggle">
            <span className="archive-toggle-label">{label}</span>
            <button
                type="button"
                className={`toggle-switch${checked ? ' toggle-switch--on' : ''}`}
                onClick={toggle}
                role="switch"
                aria-checked={checked}
                aria-label={label}
            >
                <span className="toggle-switch-thumb" />
            </button>
        </div>
    );
};

export const AllProjectsPage = () => {
    const { projects, user, includeArchived, setIncludeArchived } = useAppContext();
    const navigate = useNavigate();
    const [includeCompleted, setIncludeCompleted] = useState(false);
    
    // Filter and search state
    const [searchTerm, setSearchTerm] = useState('');
    const [stageFilter, setStageFilter] = useState('all');
    const [tagsFilter, setTagsFilter] = useState('all');
    const [sortBy, setSortBy] = useState('name');

    const hasCompletedProjects = useMemo(
        () => (projects || []).some(isProjectCompleted),
        [projects]
    );

    const visibleProjects = useMemo(
        () => filterProjectsForList(projects, { includeArchived, includeCompleted }),
        [projects, includeArchived, includeCompleted]
    );

    // Filter and sort projects
    const filteredProjects = useMemo(() => {
        let filtered = visibleProjects;
        
        // Apply search filter
        if (searchTerm) {
            filtered = filtered.filter(project => {
                const searchLower = searchTerm.toLowerCase();
                const projectName = (project.project_name || project.name || project.title || '').toLowerCase();
                const projectId = (project.id || project.project_id || '').toString().toLowerCase();
                const companyName = (project.company_name || '').toLowerCase();
                const siteAddress = (project.site_address_1 || '').toLowerCase();
                return projectName.includes(searchLower) || projectId.includes(searchLower) || companyName.includes(searchLower) || siteAddress.includes(searchLower);
            });
        }
        
        // Apply stage filter
        if (stageFilter !== 'all') {
            filtered = filtered.filter(project => 
                project.current_stage === stageFilter
            );
        }

        // Tags filter
        if (tagsFilter === 'hold') {
            filtered = filtered.filter(project => Boolean(project.manufacturing_hold));
        } else if (tagsFilter === 'engineering_hold') {
            filtered = filtered.filter(project => Boolean(project.engineering_hold));
        } else if (tagsFilter === 'shipping_hold') {
            filtered = filtered.filter(project => Boolean(project.shipping_hold));
        } else if (tagsFilter === 'expedited') {
            filtered = filtered.filter(project => Boolean(project.is_expedited));
        } else if (tagsFilter === 'pe_stamps_ordered') {
            filtered = filtered.filter(project => Boolean(project.pe_stamps_ordered));
        } else if (tagsFilter === 'op_package_ordered') {
            filtered = filtered.filter(project => Boolean(project.op_package_ordered));
        }

        // Apply sorting
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    const nameA = (a.project_name || a.name || a.title || '').toLowerCase();
                    const nameB = (b.project_name || b.name || b.title || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                case 'id':
                    return (a.id || a.project_id || 0) - (b.id || b.project_id || 0);
                case 'stage':
                    const stageA = a.current_stage || '';
                    const stageB = b.current_stage || '';
                    return stageA.localeCompare(stageB);
                case 'date':
                    const dateA = new Date(a.created_at || 0);
                    const dateB = new Date(b.created_at || 0);
                    return dateB - dateA; // Most recent first
                default:
                    return 0;
            }
        });
        
        return filtered;
    }, [visibleProjects, searchTerm, stageFilter, tagsFilter, sortBy]);

    // Get unique stages for filter dropdown
    const uniqueStages = useMemo(() => {
        const stages = new Set();
        visibleProjects.forEach(project => {
            if (project.current_stage) {
                stages.add(project.current_stage);
            }
        });
        return Array.from(stages).sort();
    }, [visibleProjects]);

    const isSuperAdmin = user?.is_super_admin === true;

    return (
        <div className="page">
            <div className="main-content">
                <div className="page-header">
                    <div className="page-header-row">
                        <h1>Project Sites</h1>
                        <div className="actions">
                            <div className="page-header-toggles">
                                {hasCompletedProjects && (
                                    <ProjectFilterToggle
                                        label="Include completed"
                                        checked={includeCompleted}
                                        onChange={setIncludeCompleted}
                                    />
                                )}
                                <ProjectFilterToggle
                                    label="Include archived"
                                    checked={includeArchived}
                                    onChange={setIncludeArchived}
                                />
                            </div>
                            <button onClick={() => navigate('/new-project')} className="btn btn-primary">
                                <FontAwesomeIcon icon={faPlus} /> New Project
                            </button>
                            {/* <DesignHelpForm project={null} /> */}
                        </div>
                    </div>
                    <hr />
                </div>

                {/* Super Admin Tools */}
                {isSuperAdmin && (
                    <div className="admin-tools admin-tools--compact">
                        <h3>Search Tools</h3>

                        <div className="admin-tools-grid">
                            {/* Search Bar */}
                            <div className="form-group">
                                <label htmlFor="search" className="form-label">
                                    Search Projects
                                </label>
                                <input
                                    id="search"
                                    type="text"
                                    placeholder="Search by name, company, address, or ID..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="form-input"
                                />
                            </div>

                            {/* Stage Filter */}
                            <div className="form-group">
                                <label htmlFor="stageFilter" className="form-label">
                                    Filter by Stage
                                </label>
                                <select
                                    id="stageFilter"
                                    value={stageFilter}
                                    onChange={(e) => setStageFilter(e.target.value)}
                                    className="form-select"
                                >
                                    <option value="all">All Stages</option>
                                    {uniqueStages.map(stage => (
                                        <option key={stage} value={stage}>{stage}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Tags */}
                            <div className="form-group">
                                <label htmlFor="tagsFilter" className="form-label">
                                    Tags
                                </label>
                                <select
                                    id="tagsFilter"
                                    value={tagsFilter}
                                    onChange={(e) => setTagsFilter(e.target.value)}
                                    className="form-select"
                                >
                                    <option value="all">All</option>
                                    <option value="hold">Manufacturing Hold</option>
                                    <option value="engineering_hold">Engineering Hold</option>
                                    <option value="shipping_hold">Shipping Hold</option>
                                    <option value="expedited">Expedited</option>
                                    <option value="pe_stamps_ordered">PE Stamps Ordered</option>
                                    <option value="op_package_ordered">Optimization Package Ordered</option>
                                </select>
                            </div>

                            {/* Sort By */}
                            <div className="form-group">
                                <label htmlFor="sortBy" className="form-label">
                                    Sort By
                                </label>
                                <select
                                    id="sortBy"
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="form-select"
                                >
                                    <option value="name">Project Name</option>
                                    <option value="id">Project ID</option>
                                    <option value="stage">Stage</option>
                                    <option value="date">Date Created (Newest First)</option>
                                </select>
                            </div>

                            {/* Clear Filters */}
                            <div className="form-group">
                                <button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setStageFilter('all');
                                        setTagsFilter('all');
                                        setSortBy('name');
                                    }}
                                    className="btn btn-secondary btn--block"
                                >
                                    Clear Filters
                                </button>
                            </div>
                        </div>

                        <p className="admin-tools-summary admin-tools-summary--bare" aria-live="polite">
                            Showing {filteredProjects.length} of {visibleProjects.length} projects
                            {searchTerm && ` (filtered by "${searchTerm}")`}
                            {stageFilter !== 'all' && ` (stage: ${stageFilter})`}
                            {tagsFilter === 'hold' && ' (tag: Manufacturing Hold)'}
                            {tagsFilter === 'engineering_hold' && ' (tag: Engineering Hold)'}
                            {tagsFilter === 'shipping_hold' && ' (tag: Shipping Hold)'}
                            {tagsFilter === 'expedited' && ' (tag: Expedited)'}
                            {tagsFilter === 'pe_stamps_ordered' && ' (tag: PE Stamps Ordered)'}
                            {tagsFilter === 'op_package_ordered' && ' (tag: Optimization Package Ordered)'}
                        </p>
                    </div>
                )}

                <ProjectsTable projects={filteredProjects} showCompanyName />
            </div>
        </div>
    )
}